/// <amd-module name="Repos/Views/Settings/Branches/Components/Branches/Branches" />
import { EnhancedGitRef } from "Data/EnhancedGitRef";
import * as React from "react";
import { branchNameToRef } from "Repos/Common/Util/Ref";
import { ISparseTreeProvider, SparseTreeFolder, SparseTreeItem } from "Repos/SparseTree/SparseTreeProvider.Types";
import * as Resources from "Repos/Views/Settings/Branches/Resources";
import { IBranchesService } from "Services/BranchesService.Types";
import {
    IObservableArray,
    IObservableLikeValue,
    IObservableValue,
    IReadonlyObservableValue,
    ObservableArray,
    ObservableLike,
    ObservableValue
} from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Card } from "VSSUI/Card";
import { FocusZone, FocusZoneContext, FocusZoneDirection, IFocusZoneContext } from "VSSUI/FocusZone";
import { Header } from "VSSUI/Header";
import { IListSelection, ListSelection } from "VSSUI/List";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { InlineKeywordFilterBarItem } from "VSSUI/TextFilterBarItem";
import { Filter, FILTER_APPLIED_EVENT, IFilter } from "VSSUI/Utilities/Filter";
import { ISelectionRange } from "VSSUI/Utilities/Selection";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { ZeroData } from "VSSUI/ZeroData";
import { SimpleGitRef } from "../../Data/SimpleGitRef";
import { IBranchTreeProvider } from "../../Provider/BranchTreeProvider.Types";
import { getBranchesService } from "../../Services/BranchesService";
import { AdditionalDataTable } from "../AdditionalDataTable/AdditionalDataTable";
import { BranchesTree } from "../BranchesTree/BranchesTree";
import { IBranchesProps } from "./Branches.Props";

const branchTextFilterKey = "text-search";

class Branches extends VssComponent<IBranchesProps, {}> {
    private additionalDataFiltered: IObservableArray<SimpleGitRef>;
    private additionalDataSelection: IListSelection;
    private additionalDataSelectionLocked = false;
    private activeRequestKey: string | undefined;
    private readonly branchesFilter: IFilter;
    private readonly branchesService: IBranchesService;
    private readonly branchesTreeProvider: IObservableValue<IBranchTreeProvider | undefined>;
    private filterDebounce: any;
    private readonly isLoading = new ObservableValue<boolean>(true);
    private selected: IReadonlyObservableValue<string | undefined>;
    private selection: IListSelection;
    private selectionLocked = false;
    private timerManagement = new TimerManagement();
    private readonly zeroDataUrl: string;

    public constructor(props: IBranchesProps, context: IVssComponentContext) {
        super(props, context);
        if (this.props.additionalData) {
            this.additionalDataFiltered = new ObservableArray<SimpleGitRef>(this.props.additionalData.slice(0));
        }
        this.branchesFilter = this.props.filter || new Filter();

        this.selection = new ListSelection();
        this.additionalDataSelection = new ListSelection();
        this.selection.selectOnFocus = false;
        this.additionalDataSelection.selectOnFocus = false;

        this.zeroDataUrl = getAssetLocation(context.pageContext, "ms.vss-code-web/settings-branches-content/Content/no-results.png");
        this.selected = this.props.selected || new ObservableValue<string | undefined>(undefined);

        this.branchesService = getBranchesService(this.context.pageContext);
        const performanceService = context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this.branchesTreeProvider = new ObservableValue(undefined);
        this.loadData(undefined).then(() => performanceService.markTimeToInteractive("Repos", "branches-hub"));
    }

    public render = () => {
        const header = (
            <div className="flex-column">
                <div className="flex-row">
                    <Header title={this.props.cardTitle || ""} className="flex-grow"></Header>
                    <div className="settings-branches-filter margin-vertical-16 margin-horizontal-16">
                        <InlineKeywordFilterBarItem
                            filter={this.branchesFilter}
                            filterItemKey={branchTextFilterKey}
                            placeholder={Resources.SearchBranchName}
                        />
                    </div>
                </div>
                {this.props.cardDescription && (
                    <div className="bolt-header-description body-m secondary-text margin-horizontal-20 margin-bottom-16 settings-branches-header-desc">
                        {this.props.cardDescription}
                    </div>
                )}
            </div>
        );

        const content = (
            <div className="settings-branches-wrapper">
                {!this.props.showInPanel && header}
                <div className="flex-column flex-grow flex-noshrink rhythm-vertical-16 padding-bottom-8">
                    <Observer branchProvider={this.branchesTreeProvider} isLoading={this.isLoading}>
                        {(observerProps: { branchProvider: IBranchTreeProvider | undefined; isLoading: boolean }) => {
                            if (!observerProps.isLoading && observerProps.branchProvider) {
                                const filterState = this.branchesFilter.getFilterItemState(branchTextFilterKey);
                                const filterValue = filterState && filterState.value;

                                return (
                                    <FocusZone direction={FocusZoneDirection.Vertical}>
                                        <div>
                                            <FocusZoneContext.Consumer>
                                                {(focusContext: IFocusZoneContext) => (
                                                    <>
                                                        {this.additionalDataFiltered && (
                                                            <AdditionalDataTable
                                                                data={this.additionalDataFiltered}
                                                                hidden={this.isLoading}
                                                                onRowActivate={this.onRowActivate}
                                                                selection={this.additionalDataSelection}
                                                                focusZoneContext={focusContext}
                                                            />
                                                        )}
                                                        {observerProps.branchProvider && (
                                                            <UncheckedObserver branchProviderBase={observerProps.branchProvider.baseProvider}>
                                                                {(innerObserverProps: { branchProviderBase: EnhancedGitRef[] }) => {
                                                                    if (
                                                                        innerObserverProps.branchProviderBase.length === 0 &&
                                                                        (!this.additionalDataFiltered || !this.additionalDataFiltered.length)
                                                                    ) {
                                                                        return (
                                                                            <BranchesZeroData
                                                                                imageUrl={this.zeroDataUrl}
                                                                                text={Resources.NoResultsText}
                                                                            />
                                                                        );
                                                                    } else {
                                                                        return (
                                                                            <BranchesTree
                                                                                ariaLabel={this.props.cardTitle}
                                                                                key={filterValue ? `filter-${filterValue}` : "all-branches"}
                                                                                branchProvider={observerProps.branchProvider!}
                                                                                selection={this.selection}
                                                                                onRowActivate={this.onRowActivate}
                                                                                focusZoneContext={focusContext}
                                                                                indexOffset={
                                                                                    this.additionalDataFiltered && this.additionalDataFiltered.length
                                                                                }
                                                                            />
                                                                        );
                                                                    }
                                                                }}
                                                            </UncheckedObserver>
                                                        )}
                                                    </>
                                                )}
                                            </FocusZoneContext.Consumer>
                                        </div>
                                    </FocusZone>
                                );
                            }

                            return <Spinner size={SpinnerSize.large} />;
                        }}
                    </Observer>
                </div>
            </div>
        );

        if (this.props.showInPanel) {
            return content;
        }
        return (
            <Card className="bolt-table-card bolt-card-white" contentProps={{ contentPadding: false }}>
                {content}
            </Card>
        );
    };

    public componentDidMount = () => {
        this.selection.subscribe(this.onSelectionChange);

        if (this.additionalDataFiltered) {
            this.additionalDataSelection.subscribe(this.onSelectionChangeAdditionalData);
        }

        this.branchesFilter.subscribe(this.filterChangeDebounce, FILTER_APPLIED_EVENT);
    };

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.selection.unsubscribe(this.onSelectionChange);

        if (this.additionalDataFiltered) {
            this.additionalDataSelection.unsubscribe(this.onSelectionChangeAdditionalData);
        }

        this.branchesFilter.unsubscribe(this.filterChangeDebounce);
    }

    private allowClick = (ref: SimpleGitRef | undefined): boolean => {
        return !!ref;
    };

    private loadData = (filterText?: string): Promise<IBranchTreeProvider | undefined> => {
        this.isLoading.value = true;
        const query = {
            projectId: this.props.projectId,
            repositoryId: this.props.repositoryId || "",
            filterText
        };

        const key = this.branchesService.getRequestKey(query);
        this.activeRequestKey = key;
        return this.branchesService.getBranchTreeProvider(query).then(result => {
            if (key === this.activeRequestKey) {
                this.isLoading.value = false;
                this.branchesTreeProvider.value = result;
                this.setSelectedIndex(true);
                return result;
            }

            return Promise.reject("stale request");
        });
    };

    private getSelected = (): string | undefined => {
        if (this.props.transformSelected) {
            return this.props.transformSelected(this.selected.value);
        } else {
            return this.selected.value;
        }
    };

    private getTreeItems(): ISparseTreeProvider<EnhancedGitRef> | undefined {
        if (this.branchesTreeProvider && this.branchesTreeProvider.value) {
            return this.branchesTreeProvider.value.baseProvider;
        }

        return undefined;
    }

    private onFilterChange = () => {
        const filterItemState = this.branchesFilter.getState()[branchTextFilterKey];
        if (filterItemState && filterItemState.value !== "") {
            this.loadData(filterItemState.value);
            const lowerCaseFilterValue = filterItemState.value.toLocaleLowerCase();
            if (this.props.additionalData) {
                this.additionalDataFiltered.value = this.props.additionalData.filter(x => filterRef(x, lowerCaseFilterValue));
            }
        } else {
            this.loadData();
            if (this.props.additionalData) {
                this.additionalDataFiltered.value = this.props.additionalData.slice(0);
            }
        }
    };

    private filterChangeDebounce = () => {
        this.filterDebounce = this.filterDebounce || this.timerManagement.debounce(this.onFilterChange, 500);
        this.filterDebounce();
    };

    private onRowActivate = (ref: SimpleGitRef) => {
        if (this.allowClick(ref)) {
            this.props.onRowActivate && this.props.onRowActivate(ref);
            this.setSelectedIndex(true);
        }
    };

    private onSelectionChange = (selectedRanges: ISelectionRange[]) => {
        if (!this.selectionLocked) {
            const treeItems = this.getTreeItems();
            if (treeItems) {
                if (selectedRanges[0]) {
                    const index = selectedRanges[0].beginIndex;
                    const endIndex = selectedRanges[0].endIndex;
                    const ref = getSimpleRef(treeItems.value[index]);
                    if (!this.allowClick(ref) || index !== endIndex) {
                        // this is not a user selection so need to see if currently selected item is in filtered list
                        this.setSelectedIndex(true);
                    } else if (ref!.ref !== this.getSelected()) {
                        this.lockAndClearSelectionAdditionalData();
                    }
                }
            }
        }
    };

    private onSelectionChangeAdditionalData = (selectedRanges: ISelectionRange[]) => {
        if (!this.additionalDataSelectionLocked && this.additionalDataFiltered && this.additionalDataFiltered.value) {
            const additionalItems = this.additionalDataFiltered.value;
            if (selectedRanges[0]) {
                const index = selectedRanges[0].beginIndex;
                const endIndex = selectedRanges[0].endIndex;
                const ref = additionalItems[index];
                if (!this.allowClick(ref) || index !== endIndex) {
                    // this is not a user selection so need to see if currently selected item is in filtered list
                    this.setSelectedIndexAdditionalData(true);
                } else if (ref.ref !== this.getSelected()) {
                    this.lockAndClearSelection();
                }
            }
        }
    };

    private lockAndClearSelection = () => {
        this.selectionLocked = true;
        this.selection.clear();
        this.selectionLocked = false;
    };

    private lockAndClearSelectionAdditionalData = () => {
        this.additionalDataSelectionLocked = true;
        this.additionalDataSelection.clear();
        this.additionalDataSelectionLocked = false;
    };

    private setSelectedIndex = (tryOtherDataSource?: boolean): boolean => {
        const treeItems = this.getTreeItems();

        if (treeItems && this.getSelected()) {
            const matchingIndex = treeItems.value.findIndex(x => getRefString(x) === this.getSelected()!);
            if (matchingIndex >= 0) {
                if (this.allowClick(getSimpleRef(treeItems.value[matchingIndex])!)) {
                    this.selection.select(matchingIndex);
                    this.lockAndClearSelectionAdditionalData();
                    return true;
                }
            } else {
                const otherSet = tryOtherDataSource && this.setSelectedIndexAdditionalData();
                if (otherSet) {
                    this.lockAndClearSelection();
                }
            }
        } else {
            this.lockAndClearSelection();
        }

        return false;
    };

    private setSelectedIndexAdditionalData = (tryOtherDataSource?: boolean): boolean => {
        if (this.additionalDataFiltered && this.additionalDataFiltered.value && this.getSelected()) {
            const additionalItems = this.additionalDataFiltered.value;
            const matchingIndex = additionalItems.findIndex(x => x.ref === this.getSelected()!);
            if (matchingIndex >= 0) {
                if (this.allowClick(additionalItems[matchingIndex])) {
                    this.additionalDataSelection.select(matchingIndex);
                    this.lockAndClearSelection();
                    return true;
                }
            } else {
                const otherSet = tryOtherDataSource && this.setSelectedIndex();
                if (otherSet) {
                    this.lockAndClearSelectionAdditionalData();
                }
            }
        } else {
            this.lockAndClearSelectionAdditionalData();
        }

        return false;
    };
}

Components.add("settings-branches-component", Branches);

interface IBranchesZeroDataProps {
    imageUrl: string;
    text: string;
}

const BranchesZeroData: React.FunctionComponent<IBranchesZeroDataProps> = props => {
    const { imageUrl, text } = props;

    return (
        <Card className="repos-branches-zero-data-card flex-center" contentProps={{ contentPadding: false }}>
            <ZeroData className="margin-top-16" imagePath={imageUrl} imageAltText={text} secondaryText={<div className="title-s">{text}</div>} />
        </Card>
    );
};

function getRefString(node: ITreeItemEx<IObservableLikeValue<SparseTreeFolder | SparseTreeItem<EnhancedGitRef> | undefined>>): string {
    const ref = getSimpleRef(node);
    return ref ? ref.ref : "";
}

function getSimpleRef(
    node: ITreeItemEx<IObservableLikeValue<SparseTreeFolder | SparseTreeItem<EnhancedGitRef> | undefined>>
): SimpleGitRef | undefined {
    const sparseNode = ObservableLike.getValue(node.underlyingItem.data);
    if (sparseNode) {
        if (sparseNode.kind === "item") {
            return sparseNode.data;
        } else {
            return { ref: branchNameToRef(sparseNode.absoluteIdentifier) + "/", isFolder: true };
        }
    }
    return undefined;
}

function filterRef(ref: SimpleGitRef, filter: string): boolean {
    const compareString = (ref.displayText || ref.ref).toLocaleLowerCase();
    return compareString.indexOf(filter) >= 0;
}
